/**
 * Country dial codes for the phone input, and the E.164 arithmetic around them.
 *
 * The registry is MessageEagle World Data (`GET /worlddata/countries` — its
 * `dialcodes.All`), synced into ./dialCodes.json by `scripts/sync-dial-codes.mjs`.
 * One registry for the number the applicant types here and the SMS MessageEagle
 * then sends to it (`backend/crates/adapters/message-eagle/src/lib.rs`, which requires E.164).
 *
 * Names are localised by the browser (`Intl.DisplayNames`), so the Thai UI lists
 * `ไทย`, not `Thailand`, with no translation file to keep in step; the registry's
 * English name is only the fallback for a code the browser cannot name.
 */
import registry from './dialCodes.json';

export interface Country {
  /** ISO-3166 alpha-2, uppercase. */
  code: string;
  /** E.164 calling code with the plus — NANP territories keep their full prefix (`+1684`). */
  dial: string;
  /** Display name in the UI language. */
  name: string;
  /** The registry's English name — also searchable, whatever the UI language. */
  english: string;
}

export const DEFAULT_COUNTRY = 'TH';
/** E.164 allows at most 15 digits after the plus. */
const E164_MAX_DIGITS = 15;

const ROWS = registry as [string, string, string][];

/** Every country, named in `language` and sorted the way that language sorts. */
export function countries(language: string): Country[] {
  let names: Intl.DisplayNames | undefined;
  try {
    names = new Intl.DisplayNames([language], { type: 'region' });
  } catch {
    names = undefined;
  }
  const collator = new Intl.Collator(language);
  return ROWS.map(([code, dial, english]) => ({
    code,
    dial,
    english,
    name: names?.of(code) ?? english,
  })).sort((a, b) => collator.compare(a.name, b.name));
}

/** The country for `code`, else the default, else the registry's first row —
 *  never `undefined`: the field always shows SOME dial code. */
export function countryOf(list: Country[], code: string): Country {
  const found =
    list.find((c) => c.code === code) ?? list.find((c) => c.code === DEFAULT_COUNTRY) ?? list[0];
  if (!found) throw new Error('dial-code registry is empty');
  return found;
}

/** Countries matching `query` by localised name, English name, ISO code or dial code. */
export function searchCountries(list: Country[], query: string): Country[] {
  const q = query.trim().toLowerCase();
  if (q === '') return list;
  const digits = q.replace(/^\+/, '');
  return list.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.english.toLowerCase().includes(q) ||
      c.code.toLowerCase() === q ||
      (/^\d+$/.test(digits) && c.dial.slice(1).startsWith(digits)),
  );
}

/**
 * The E.164 number for a national number typed under `dial`, or '' when there is
 * nothing to send. One leading trunk `0` is dropped (`081…` under +66 → `+6681…`),
 * except under +39, where Italian landlines keep it.
 */
export function toE164(dial: string, national: string): string {
  let digits = national.replace(/\D/g, '');
  if (digits.startsWith('0') && dial !== '+39') digits = digits.slice(1);
  if (digits === '') return '';
  const full = `${dial.slice(1)}${digits}`.slice(0, E164_MAX_DIGITS);
  return `+${full}`;
}

/**
 * Split a stored or pasted `+…` number back into country + national part, by the
 * LONGEST matching dial code — `+1684…` is American Samoa, not the `+1` of the
 * US. A bare `+1` resolves to the US (MessageEagle's default too): the prefix
 * alone cannot tell it from Canada, and the answer must not depend on how the
 * localised list happens to sort.
 */
export function splitE164(
  value: string,
  list: Country[],
): { code: string; national: string } | null {
  const digits = value.replace(/[^\d+]/g, '');
  if (!digits.startsWith('+')) return null;
  const body = digits.slice(1);
  let best: Country | undefined;
  for (const c of list) {
    if (!body.startsWith(c.dial.slice(1))) continue;
    const longer = !best || c.dial.length > best.dial.length;
    const tieToUs = best?.dial.length === c.dial.length && c.code === 'US';
    if (longer || tieToUs) best = c;
  }
  return best ? { code: best.code, national: body.slice(best.dial.length - 1) } : null;
}

/** `/flags/th.svg` — served from this origin (see the sync script for why). */
export function flagUrl(code: string): string {
  return `${import.meta.env.BASE_URL}flags/${code.toLowerCase()}.svg`;
}
